import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Calendar, FolderOpen, Mail, Mic, Loader2, Link2, Unlink, AlertTriangle, KeyRound, ListChecks } from "lucide-react";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";

/** De koppelingen met Google. R57 fase 1, uitgebreid in fase 2 en 3.
 *
 *  DRIE DINGEN STAAN HIER IN WOORDEN OP HET SCHERM, en geen van drieen is
 *  opsmuk:
 *
 *    DIT WERKT ALLEEN OP DE DESKTOP. Besluit 1 van 25 augustus, met Franks
 *    eigen woorden erbij: "ik weet wat het kost: geen dagprogramma op mijn
 *    telefoon." De koppeling luistert op 127.0.0.1 van DEZE machine, dus in de
 *    PWA op zijn telefoon gaat de deur nergens open. Dat hoort te staan waar de
 *    knop staat en niet in een ontwerpdocument.
 *
 *    WAT ER WORDT GEVRAAGD STAAT ER PER BRON BIJ. Een mens die op "koppelen"
 *    klikt hoort voor de klik te weten wat hij weggeeft, en niet erna in een
 *    scherm van Google.
 *
 *    ONTKOPPELEN TREKT DE TOEGANG OOK BIJ GOOGLE IN. Anders leest "ontkoppeld"
 *    als "Google kan er niet meer bij" terwijl dat niet zo is.
 *
 *  ELKE BRON HEEFT EEN EIGEN KOPPELING EN EEN EIGEN TOESTEMMING. Franks eis van
 *  26 augustus 2026, met een gemeten reden: Google ondersteunt geen
 *  incrementele toestemming voor geinstalleerde apps, dus een bestaande
 *  toestemming valt niet uit te breiden. Drie losse koppelingen maken van "de
 *  agenda blijft ongemoeid" een eigenschap in plaats van een belofte.
 *
 *  DE LIJST BRONNEN KOMT VAN DE BACKEND EN STAAT HIER NIET. Een scherm dat zijn
 *  eigen drie regels hardcodeert, is het scherm dat bij een vierde bron zwijgt
 *  terwijl de route hem allang aanbiedt.
 */

/** Het pictogram per bron. Lijn-iconen, nooit emoji (BRAND.md). */
const ICOON = {
  google_calendar: Calendar,
  google_drive: FolderOpen,
  gmail: Mail,
  fathom: Mic,
  tasq: ListChecks,
};

/** DE TWEE MANIEREN WAAROP EEN BRON ZICH LEGITIMEERT, en het scherm hoort ze
 *  uit elkaar te houden omdat de HANDELING verschilt: bij de een klik je en
 *  opent er een browser, bij de ander plak je een sleutel.
 *
 *  DIT WOORD KOMT VAN DE BACKEND (`bron.legitimatie`) en staat hier alleen als
 *  constante, zodat er geen losse letterlijke strings door dit bestand zwerven.
 *  WELKE bron welke soort is, wordt hier niet beslist: dat zou een tweede lijst
 *  zijn die uit de pas gaat lopen met het register.
 */
const MET_SLEUTEL = "sleutel";

/** Wat NovaMund met deze bron doet, in gewone woorden, voor de klik. */
const UITLEG = {
  google_calendar:
    // Fout 2 (glasronde v0.9.158): sinds C2a schrijft NovaMund wel, na een kaart.
    "Je agenda, gelezen op deze machine voor je dag. Een afspraak zet NovaMund er " +
    "alleen in nadat jij hem op een kaart hebt bevestigd.",
  google_drive:
    "Welke documenten je vandaag wijzigde, met naam en tijd. Alleen lezen, " +
    "en de inhoud van je bestanden gaat niet naar het model.",
  gmail:
    "Je mail van de dag, en een antwoord op een bericht als je daarom vraagt. " +
    "Elk antwoord zie je eerst helemaal, en het gaat pas weg na jouw bevestiging.",
  fathom:
    "Je Fathom-opnamen en hun transcripten, gelezen met een sleutel die je hier " +
    "plakt. De sleutel staat versleuteld op deze machine en nergens anders.",
  tasq:
    "Je TASQ-taken, zodat ze bij je dag horen: wat er moet en waar je aan " +
    "werkt, in elke werkruimte die je token dekt. Aanmaken en wijzigen gaat " +
    "altijd via een kaart die je eerst bevestigt. Het token staat versleuteld " +
    "op deze machine en nergens anders.",
};

/**
 * Wat er na ontkoppelen in de melding staat, naar wat de backend bij Google deed. PUUR.
 * Gmail-herstel (24 okt 2026): Google trekt de toestemming voor alle Google-bronnen samen in,
 * dus zolang er nog een andere Google-bron hangt, wordt deze alleen hier gewist.
 */
export function ontkoppelZin(bron, bijGoogle, t = (x, v) => x.replace(/\{(\w+)\}/g, (_, k) => v?.[k] ?? "")) {
  const v = { bron: bron.naam };
  if (bron.legitimatie === MET_SLEUTEL) return t("{bron} is hier ontkoppeld", v);
  if (bijGoogle === "al_weg") return t("{bron} is ontkoppeld; bij Google was de toegang al vervallen", v);
  if (bijGoogle === "gedeeld") return t("{bron} is hier ontkoppeld. Bij Google blijft de toestemming staan, omdat je andere Google-koppelingen die delen.", v);
  return t("{bron} is ontkoppeld, ook bij Google", v);
}

export default function GoogleConnection() {
  const { t } = useTaal();
  const [laden, setLaden] = useState(true);
  const [clientAanwezig, setClientAanwezig] = useState(false);
  const [bronnen, setBronnen] = useState([]);
  const [koppelingen, setKoppelingen] = useState([]);
  const [bezig, setBezig] = useState("");
  const [wachtOpJou, setWachtOpJou] = useState("");
  const [nietGeladen, setNietGeladen] = useState(false);
  // **DE DERDE TOESTAND, EN ZIJ HOORT OP DE KAART.** Een toast verdwijnt na een
  // paar seconden, en dan staat er een kaart die eruitziet als "niet gekoppeld"
  // terwijl er zojuist iets is geweigerd. Wie zijn sleutel verkeerd plakt, hoort
  // de reden te blijven zien zolang hij hem niet heeft gecorrigeerd.
  const [fout, setFout] = useState({});
  // De ingetikte sleutel, per bron. In de STAAT en niet in de rij: hij verlaat
  // dit scherm alleen bij het opslaan.
  const [sleutels, setSleutels] = useState({});

  const haal = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/connections`);
      setClientAanwezig(r.data?.client_aanwezig === true);
      setBronnen(Array.isArray(r.data?.bronnen) ? r.data.bronnen : []);
      setKoppelingen(Array.isArray(r.data?.koppelingen) ? r.data.koppelingen : []);
      setNietGeladen(false);
    } catch {
      // **TOT FASE 2 ZWEEG DIT, EN DAT MOCHT.** Het paneel droeg toen EEN
      // vastgeschreven blok, dus een gevallen route liet nog altijd Google
      // Calendar met zijn knop staan; er viel niets te melden.
      //
      // Sinds de bronnenlijst van de backend komt, betekent zwijgen een LEEG
      // tabblad, en dat leest als "er valt hier niets te koppelen". Dat is de
      // vorm waar dit hele project tegen bestaat: een gat dat eruitziet als een
      // antwoord. Dus wordt het gezegd.
      setNietGeladen(true);
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => { haal(); }, [haal]);

  const koppel = async (bron, { vervang = false } = {}) => {
    setBezig(bron.sleutel);
    try {
      // Fout 1 (glasronde v0.9.158): opnieuw koppelen VERVANGT de bestaande
      // koppeling; de backend trekt de oude pas in na een geslaagde nieuwe.
      const start = await axios.post(
        `${API}/connections/google/start/${bron.sleutel}${vervang ? "?vervang=1" : ""}`);
      // De schil vangt elke URL buiten de app en opent hem in de ECHTE browser
      // (`setWindowOpenHandler`, desktop/main.js). Geen eigen venster zonder
      // adresbalk: wie zijn Google-wachtwoord typt, hoort te kunnen zien waar.
      window.open(start.data.url, "_blank", "noopener");
      setWachtOpJou(bron.sleutel);
      const klaar = await axios.post(`${API}/connections/google/finish/${start.data.id}`);
      setKoppelingen(vorige => [klaar.data, ...vorige.filter(k => k.id !== klaar.data.id
        && !(vervang && k.bron === bron.sleutel))]);
      toast.success(t("{bron} gekoppeld", { bron: bron.naam }));
    } catch (e) {
      // De melding van de backend in de taal van het scherm (fout 1).
      toast.error(e.response?.data?.detail ? t(e.response.data.detail) : t("Koppelen is niet gelukt"));
    } finally {
      setBezig("");
      setWachtOpJou("");
    }
  };

  /** Een bron met een vaste sleutel koppelen. Post 7, 5 september 2026.
   *
   *  **GEEN BROWSER EN GEEN TWEEDE AANROEP.** Bij OAuth staat de tweede aanroep
   *  minutenlang open terwijl er een mens in een browsertab staat te lezen; hier
   *  is het EEN verzoek dat meteen antwoord geeft, want de backend toetst de
   *  sleutel met een echte aanroep voordat hij hem opslaat.
   *
   *  **DE SLEUTEL WORDT UIT DE STAAT GEWIST ZODRA HIJ IS OPGESLAGEN.** Hij staat
   *  dan versleuteld in de database; hem hier laten staan zou een tweede kopie
   *  zijn die niemand nodig heeft.
   */
  const bewaarSleutel = async (bron) => {
    setBezig(bron.sleutel);
    setFout(vorige => ({ ...vorige, [bron.sleutel]: "" }));
    try {
      const r = await axios.put(`${API}/connections/sleutel/${bron.sleutel}`,
                                { sleutel: sleutels[bron.sleutel] || "" });
      setKoppelingen(vorige => [r.data, ...vorige.filter(k => k.id !== r.data.id)]);
      setSleutels(vorige => ({ ...vorige, [bron.sleutel]: "" }));
      toast.success(t("{bron} gekoppeld", { bron: bron.naam }));
    } catch (e) {
      const reden = e.response?.data?.detail || t("De sleutel opslaan is niet gelukt");
      setFout(vorige => ({ ...vorige, [bron.sleutel]: reden }));
      toast.error(reden);
    } finally {
      setBezig("");
    }
  };

  const ontkoppel = async (id, bron) => {
    setBezig(bron.sleutel);
    try {
      const r = await axios.delete(`${API}/connections/${id}`);
      setKoppelingen(vorige => vorige.map(k => (k.id === id ? r.data : k)));
      toast.success(ontkoppelZin(bron, r.data?.bij_google, t));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Ontkoppelen is niet gelukt"));
    } finally {
      setBezig("");
    }
  };

  if (laden) {
    return (
      <div className="flex items-center justify-center py-8" data-testid="connections-laden">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="google-connections">
      {/* DE PRIJS VAN BESLUIT 1, EEN KEER EN BOVENAAN. Hij geldt voor alle
          bronnen, dus hem per bron herhalen zou hem tot ruis maken. */}
      <p className="text-xs text-muted-foreground" data-testid="alleen-desktop">
        {t("Koppelen met Google kan alleen in de desktop-app. Die koppeling luistert op deze machine, dus vanaf de app op je telefoon kan het niet. Een bron die je met een sleutel koppelt, heeft die beperking niet.")}
      </p>

      {nietGeladen ? (
        <p className="text-xs text-[hsl(var(--accent))]" data-testid="niet-geladen">
          <AlertTriangle className="h-3 w-3 inline mr-1" />
          {t("De koppelingen konden niet worden geladen, dus deze lijst klopt niet met wat je hebt. Er is niets gewijzigd. Open dit tabblad opnieuw om het nog eens te proberen.")}
        </p>
      ) : !clientAanwezig && bronnen.some(b => b.legitimatie !== MET_SLEUTEL) && (
        <p className="text-xs text-[hsl(var(--accent))]" data-testid="geen-client">
          <AlertTriangle className="h-3 w-3 inline mr-1" />
          {t("Deze installatie heeft geen Google-client, dus de Google-bronnen kunnen nog niet worden gekoppeld. Bronnen met een sleutel hebben daar geen last van.")}
        </p>
      )}

      {bronnen.map(bron => {
        const Icoon = ICOON[bron.sleutel] || Calendar;
        const actief = koppelingen.find(k => k.bron === bron.sleutel && k.actief);
        const eerder = koppelingen.filter(k => k.bron === bron.sleutel && !k.actief);
        const bezigHier = bezig === bron.sleutel;
        // **DE SOORT BEPAALT DE HANDELING EN DE VOORWAARDE.** Een sleutelbron
        // heeft geen Google-client nodig; die eis geldt alleen voor de bronnen
        // die door het toestemmingsscherm van Google gaan.
        const metSleutel = bron.legitimatie === MET_SLEUTEL;
        const bruikbaar = metSleutel || clientAanwezig;
        const foutHier = fout[bron.sleutel] || "";

        return (
          <div key={bron.sleutel} className="border rounded-lg p-4 space-y-3"
               data-testid={`bron-${bron.sleutel}`}>
            <div className="flex items-start gap-3">
              <Icoon className="h-4 w-4 mt-0.5 text-[hsl(var(--accent))] shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{bron.naam}</p>
                <p className="text-xs text-muted-foreground" data-testid={`uitleg-${bron.sleutel}`}>{UITLEG[bron.sleutel] ? t(UITLEG[bron.sleutel]) : null}</p>
              </div>
            </div>

            {/* **DE DERDE TOESTAND, ZICHTBAAR EN NIET VLUCHTIG.** Zij staat
                BOVEN de knop, want zij gaat over de poging die je zojuist deed
                en niet over de knop die je nog kunt indrukken. */}
            {foutHier && (
              <p className="text-xs text-[hsl(var(--accent))]"
                 data-testid={`fout-${bron.sleutel}`}>
                <AlertTriangle className="h-3 w-3 inline mr-1" />
                {foutHier}
              </p>
            )}

            {bruikbaar && (actief ? (
              <div className="space-y-2" data-testid={`actief-${bron.sleutel}`}>
                <p className="text-xs">
                  {/* **"as" IS WEG EN DAAR STAAT EEN PUNT, en dat is geen
                      opmaak maar een reparatie.** Het accountveld draagt sinds
                      post 10a niet altijd een adres: bij TASQ staat er een
                      TELLING ("2 workspaces"), want zijn toetsroute geeft geen
                      profiel terug. "Connected as 2 workspaces" is onzin;
                      "Connected - 2 workspaces" klopt voor allebei. */}
                  {t("Gekoppeld")}{actief.account ? ` - ${actief.account}` : ""}
                  {actief.gekoppeld_op ? ` ${t("sinds {wanneer}", { wanneer: actief.gekoppeld_op.slice(0, 10) })}` : ""}.
                </p>
                {/* C2a: de koppeling mist een toestemming die de bron nu vraagt
                    (de agenda vraagt sinds 15 oktober 2026 ook `calendar.events`).
                    Opnieuw koppelen is dezelfde stroom als koppelen; Frank doet
                    dat zelf op glas en Google vraagt dan om de extra toestemming. */}
                {/* GMAIL-HERSTEL (24 okt 2026): Google erkent de sleutel niet meer. Dezelfde
                    stroom als koppelen; de nieuwe koppeling vervangt deze. */}
                {actief.verlopen && (
                  <div className="rounded border border-dashed p-2 space-y-1"
                       data-testid={`verlopen-${bron.sleutel}`}>
                    <p className="text-xs">
                      <AlertTriangle className="h-3 w-3 inline mr-1" />
                      {t("De toegang tot {bron} is verlopen of ingetrokken bij Google. Koppel opnieuw om weer te kunnen lezen.", { bron: bron.naam })}
                    </p>
                    <Button size="sm" disabled={!!bezig} onClick={() => koppel(bron, { vervang: true })}
                            data-testid={`opnieuw-verlopen-${bron.sleutel}`}>
                      <Link2 className="h-3 w-3 mr-1" />
                      {t("Opnieuw koppelen")}
                    </Button>
                  </div>
                )}
                {!actief.verlopen && actief.mist_omvang?.length > 0 && (
                  <div className="rounded border border-dashed p-2 space-y-1"
                       data-testid={`mist-omvang-${bron.sleutel}`}>
                    <p className="text-xs">
                      {t("Deze koppeling mist een toestemming die NovaMund nu vraagt. Koppel opnieuw; Google vraagt je dan om die extra toestemming.")}
                    </p>
                    <Button size="sm" disabled={!!bezig} onClick={() => koppel(bron, { vervang: true })}
                            data-testid={`opnieuw-${bron.sleutel}`}>
                      <Link2 className="h-3 w-3 mr-1" />
                      {t("Opnieuw koppelen")}
                    </Button>
                  </div>
                )}
                <Button variant="outline" size="sm" disabled={!!bezig}
                        onClick={() => ontkoppel(actief.id, bron)}
                        data-testid={`ontkoppel-${bron.sleutel}`}>
                  {bezigHier ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                             : <Unlink className="h-3 w-3 mr-1" />}
                  {t("Ontkoppelen")}
                </Button>
                <p className="text-xs text-muted-foreground">
                  {metSleutel
                    // **WAT ONTKOPPELEN HIER WEL EN NIET DOET, en dat verschil
                    // hoort er te staan.** Bij Google wordt de toegang ECHT
                    // ingetrokken; een sleutel wordt hier vergeten en blijft bij
                    // de leverancier geldig tot je hem daar intrekt. Het
                    // omgekeerde beweren zou een leugen zijn op precies het
                    // onderwerp waar iemand op vertrouwt.
                    ? `Ontkoppelen vergeet de sleutel hier. Hij blijft geldig bij ` +
                      `${bron.naam} tot je hem daar in je account intrekt.`
                    : t("Ontkoppelen trekt de toegang ook bij Google in, niet alleen hier.")}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {/* **DE HANDELING VERSCHILT PER SOORT, EN DE SOORT KOMT VAN DE
                    BACKEND.** Een scherm dat zelf weet welke bron een sleutel
                    heeft, is een tweede lijst die uit de pas gaat lopen met het
                    register - precies waarom de bronnenlijst hier al van de
                    backend kwam. */}
                {metSleutel ? (
                  <div className="flex items-center gap-2"
                       data-testid={`sleutelveld-${bron.sleutel}`}>
                    <Input
                      type="password"
                      autoComplete="off"
                      placeholder={`Plak je API-sleutel van ${bron.naam}`}
                      value={sleutels[bron.sleutel] || ""}
                      disabled={!!bezig}
                      onChange={e => setSleutels(vorige => (
                        { ...vorige, [bron.sleutel]: e.target.value }))}
                      className="h-8 text-xs min-w-0 flex-1"
                      data-testid={`sleutelinvoer-${bron.sleutel}`}
                    />
                    <Button size="sm" className="shrink-0"
                            disabled={!!bezig || !(sleutels[bron.sleutel] || "").trim()}
                            onClick={() => bewaarSleutel(bron)}
                            data-testid={`bewaar-${bron.sleutel}`}>
                      {bezigHier ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                 : <KeyRound className="h-3 w-3 mr-1" />}
                      {t("Sleutel opslaan")}
                    </Button>
                  </div>
                ) : (
                <Button size="sm" disabled={!!bezig} onClick={() => koppel(bron)}
                        data-testid={`koppel-${bron.sleutel}`}>
                  {bezigHier ? <Loader2 className="h-3 w-3 animate-spin mr-1" />
                             : <Link2 className="h-3 w-3 mr-1" />}
                  {t("Koppel {naam}", { naam: bron.naam })}
                </Button>
                )}
                {/* **WAT ER MET DE SLEUTEL GEBEURT, VOOR HET PLAKKEN.** Wie een
                    sleutel weggeeft hoort te weten waar hij belandt, en niet
                    erna in een instellingenscherm te moeten zoeken. */}
                {metSleutel && (
                  <p className="text-xs text-muted-foreground"
                     data-testid={`sleuteluitleg-${bron.sleutel}`}>
                    {t("De sleutel wordt eerst bij {naam} gecontroleerd, dus een verkeerde sleutel wordt hier geweigerd en niet uren later. Hij staat versleuteld op deze machine en gaat nooit naar het model.", { naam: bron.naam })}
                  </p>
                )}
                {/* WAT ER GEBEURT TERWIJL HET GEBEURT. De tweede aanroep staat
                    lang open; zonder deze zin bevriest het scherm zonder woord. */}
                {wachtOpJou === bron.sleutel && (
                  <p className="text-xs text-muted-foreground"
                     data-testid={`wacht-${bron.sleutel}`}>
                    {t("Er is een browsertabblad geopend. Zeg daar ja, dan komt dit vanzelf terug.")}
                  </p>
                )}
              </div>
            ))}

            {/* WAT ER OOIT HEEFT GEHANGEN BLIJFT LEESBAAR. Weglaten zou de vraag
                "heeft NovaMund hier ooit bij gekund" onbeantwoordbaar maken. */}
            {eerder.length > 0 && (
              <div className="pt-2 border-t" data-testid={`eerder-${bron.sleutel}`}>
                <p className="text-xs text-muted-foreground mb-1">{t("Eerdere koppelingen")}</p>
                {eerder.map(k => (
                  <p key={k.id} className="text-xs text-muted-foreground">
                    {t("{van} tot {tot}, ingetrokken.", { van: k.gekoppeld_op?.slice(0, 10), tot: k.ingetrokken_op?.slice(0, 10) })}
                  </p>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
